import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/api/client";

export default function SearchBox({ variant = "desktop" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (!query) { setResults([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query, limit: 8 } });
        setResults(data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    setOpen(false);
    nav(`/search?q=${encodeURIComponent(query)}`);
  };

  const goToProduct = (id) => { setOpen(false); setQ(""); nav(`/product/${id}`); };

  const widthCls = variant === "mobile" ? "w-full" : "w-64 xl:w-44";

  return (
    <div ref={boxRef} className={`relative ${widthCls}`} data-testid={`search-box-${variant}`}>
      <form onSubmit={submit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            data-testid={`search-input-${variant}`}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q.trim() && setOpen(true)}
            placeholder="Search products…"
            className="w-full h-9 pl-9 pr-8 rounded-full bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:border-[#F48E2E] focus:bg-white"
          />
          {q && (
            <button
              type="button"
              data-testid={`search-clear-${variant}`}
              onClick={() => { setQ(""); setResults([]); setOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </form>

      {open && (
        <div
          data-testid={`search-dropdown-${variant}`}
          className={`absolute ${variant === "mobile" ? "left-0 right-0" : "right-0 w-80"} top-full mt-2 rounded-2xl border border-gray-200 bg-white shadow-lg z-50 overflow-hidden max-h-[70vh] overflow-y-auto`}
        >
          {loading ? (
            <div className="p-4 text-xs text-gray-500">Searching…</div>
          ) : results.length === 0 ? (
            <div data-testid={`search-empty-${variant}`} className="p-4 text-sm text-gray-500">No products found for your search.</div>
          ) : (
            <>
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`search-result-${p.id}`}
                  onClick={() => goToProduct(p.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#FEF3E6] text-left border-b border-gray-100 last:border-0"
                >
                  {p.image_base64 ? (
                    <img src={p.image_base64} alt={p.name} className="h-10 w-10 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded bg-gray-100 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">{p.vendor_business_name || "CityShor Exhibitor"}</div>
                  </div>
                  <div className="text-sm font-mono-tab whitespace-nowrap">₹{Number(p.price || 0).toLocaleString("en-IN")}</div>
                </button>
              ))}
              <button
                type="button"
                data-testid={`search-see-all-${variant}`}
                onClick={submit}
                className="w-full text-center text-xs text-[#F48E2E] hover:bg-[#FEF3E6] py-2 border-t border-gray-100"
              >
                See all results for "{q.trim()}"
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
